import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import { DxDataGridComponent } from 'devextreme-angular';
import { GoldenLayoutContainer } from 'ngx-golden-layout';
import GoldenLayout from 'golden-layout';
import { durationToHoursStr, durationToStr, TrailerIntervention, TransportIntervention, User, notify } from 'shared';
import { Subscription } from 'rxjs';
import tagsIcon from '@iconify/icons-mdi/id-card';
import reportIcon from '@iconify/icons-mdi/chart-areaspline';
import { FieldsInterventionsPageRequest } from '../fields-interventions-page/fields-interventions-page-request';
import { FieldsInterventionsNavigationActivatedComponent } from '../fields-interventions-navigation/fields-interventions-navigation-activated-component';
import { find, uniqBy } from 'lodash';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { TrailerInterventionService } from '../../stores/trailer-intervention.service';
import { ReconstructedInterventionService, ReconstructedIntervention } from '../../stores/reconstructed-intervention.service';
import { CropService } from '../../stores/crop.service';
import { UserDomainService } from '../../user-domains/user-domain.service';
import { TrailerService } from '../../stores/trailer.service';
import ArrayStore from 'devextreme/data/array_store';
import CustomStore from 'devextreme/data/custom_store';
import { LoadOptions } from 'devextreme/data/load_options';
import { HttpParams } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { custom } from 'devextreme/ui/dialog';


@Component({
  selector: 'app-fields-interventions-grid',
  templateUrl: './fields-interventions-grid.component.html',
  styleUrls: ['./fields-interventions-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldsInterventionsGridComponent extends FieldsInterventionsNavigationActivatedComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  ds: DataSource;
  canEdit: boolean;
  
  // Function to check if rating can be edited (only for trailer interventions without rating)
  canEditRating = (rowData: any) => {
    if (!rowData) {
      return false;
    }
    
    // For allowEditing function, rowData is the actual row data object
    const intervention = rowData.intervention_source ? rowData : (rowData.row?.data || rowData.data || rowData);
    if (!intervention) {
      return false;
    }
    
    // Only allow editing for trailer interventions (including manual) that don't have a rating yet
    const isTrailer = intervention.intervention_source === 'trailer' || intervention.intervention_source === 'manual';
    const hasRating = intervention.data_accuracy_rating && 
                      intervention.data_accuracy_rating !== null && 
                      intervention.data_accuracy_rating !== '';
    
    return isTrailer && !hasRating && this.canEdit;
  }
  canAdd: boolean;
  canDelete: boolean;
  canValidate: boolean;
  canReject: boolean;
  canRevoke: boolean;
  tagsIcon = tagsIcon;
  reportIcon = reportIcon;
  current: TrailerIntervention | ReconstructedIntervention;
  selectedReconstructedIntervention: ReconstructedIntervention;
  tagDetailsEl: HTMLElement;
  tagDetailsVisible: boolean;
  reportVisible: boolean;
  reconstructedReportVisible: boolean = false;
  
  // Accuracy rating options for trailer interventions
  accuracyRatingOptions = [
    { value: 'EXACT', label: 'Données exactes' },
    { value: 'APPROXIMATELY_CORRECT', label: 'Données approximativement correctes' },
    { value: 'INEXACT', label: 'Données inexactes' }
  ];

  @Input()
  request: FieldsInterventionsPageRequest;

  @Input()
  width: number;

  @Input()
  height: number;

  @Input()
  @HostBinding('hidden')
  hidden: boolean;
  private submitSub: Subscription;
  private hasPendingRequest: boolean;

  @ViewChild(DxDataGridComponent, { static: true })
  grid: DxDataGridComponent;
  @ViewChild('validateDialog', { static: false }) validateDialog: any;
  @ViewChild('rejectDialog', { static: false }) rejectDialog: any;
  @ViewChild('ratingDialog', { static: false }) ratingDialog: any;
  @ViewChild('manualInterventionDialog', { static: false }) manualInterventionDialog: any;
  gridCss: { width: string; height: string };
  dateFormat = 'shortDateShortTime';
  attributesNames: string[] = [];
  attributesValues: { [key: string]: { text: string, value: string | [] } } = {};

  columnFocusTabsModes = [
    { text: 'Outils', id: 'trailer' },
    { text: 'Parcelle', id: 'field' },
    { text: 'Temps', id: 'times' },
    { text: 'Carburants', id: 'fuel' },
    { text: 'Vitesse', id: 'speed' },
    { text: 'Pression', id: 'pressure' },
  ];
  columnFocusCurrentModes = ['trailer', 'field'];
  crops: Promise<any>;
  domains: any;
  bu: any;

  sum = 0;
  calculateCellValue = (rowData) => {
    this.sum += 1; // = rowData.working_area_percent;
    return this.sum;
  }

  trailersDataSource: any;

  constructor(
    private trailerInterventionService: TrailerInterventionService,
    private reconstructedInterventionService: ReconstructedInterventionService,
    private cropService: CropService,
    private userDomainService: UserDomainService,
    private trailerService: TrailerService,
    @Inject(GoldenLayoutContainer) private container: GoldenLayout.Container,
    public user: User,
    private cdr: ChangeDetectorRef,
  ) {
    super();

    // Fetch crops
    this.crops = cropService.newDataSource({ paginate: false }).load();
    this.domains = {
      store: new ArrayStore({
        data: userDomainService.domains,
        key: 'id',
      }),
      sort: 'name',
    };
    this.bu = {
      store: new ArrayStore({
        data: uniqBy(userDomainService.domains.map((d: any) => {
          return { id: d.business_unit_id, name: d.business_unit_name };
        }), (b) => b.id),
        key: 'id',
      }),
      sort: 'name',
    };
  }

  onContentReady() {
    this.sum = 0;
  }

  ngOnInit() {
    // Dims
    this.gridCss = {
      width: this.width + 'px',
      height: (this.height - 56) + 'px',
    };
    // Initialize permissions
    this.canValidate = this.user.can('validate reconstructed_interventions');
    this.canReject = this.user.can('reject reconstructed_interventions');
    this.canRevoke = this.user.can('validate reconstructed_interventions'); // Same permission as validate
    this.canAdd = this.user.can('create trailer_interventions');
    this.canDelete = this.user.can('delete trailer_interventions');
    this.canEdit = this.user.can('create trailer_interventions'); // Use create permission for editing

    // Load trailers for dropdown (respects domain filtering automatically)
    this.trailersDataSource = this.trailerService.newDataSource({ paginate: false });
    console.log('Permissions initialized:', { 
      canValidate: this.canValidate, 
      canReject: this.canReject, 
      canAdd: this.canAdd,
      canDelete: this.canDelete 
    });
  }

  showTags(e: MouseEvent, data: TrailerIntervention) {
    e.preventDefault();
    this.current = data;
    this.tagDetailsEl = e.target as HTMLElement;
    this.tagDetailsVisible = true;
  }

  durationToHoursStr(cellInfo) {
    return durationToHoursStr(cellInfo.value);
  }

  durationToStr(cellInfo) {
    return durationToStr(cellInfo.value);
  }

  getStartTime = (rowData: any) => {
    return rowData.start_time;
  }

  showInterventionDetails = (e: any) => {
    // Handle DevExtreme button click event
    // e.row.data contains the row data
    const intervention = e?.row?.data || e?.data;
    if (!intervention) {
      console.error('No intervention data found in event:', e);
      return;
    }
    
    console.log('Showing intervention details:', intervention);
    
    // Ensure trailer interventions are proper TrailerIntervention instances
    if (this.isTrailer(intervention)) {
      // If it's not already a TrailerIntervention instance, create one
      if (!(intervention instanceof TrailerIntervention)) {
        this.current = new TrailerIntervention(intervention);
      } else {
        this.current = intervention;
      }
      this.reportVisible = true;
    } else if (this.isReconstructed(intervention)) {
      this.selectedReconstructedIntervention = intervention as ReconstructedIntervention;
      this.reconstructedReportVisible = true;
    } else {
      this.current = intervention;
      this.reportVisible = true;
    }
    this.cdr.markForCheck();
  }

  private loadRequest() {
    // Always recreate the data source to ensure fresh data
    // This ensures fresh data is loaded on each "generate" click
    if (this.ds) {
      this.ds.dispose();
    }
    
    // Create merged data source
    const mergedStore = new CustomStore({
      key: 'id',
      load: (loadOptions: LoadOptions) => {
        // Get date filters from request
        const fromDate = this.request.from;
        const toDate = this.request.to;

        // Don't apply pagination here - we need all data to merge properly
        // The grid will handle pagination after merging
        // Get intervention type filter
        const interventionType = this.request.interventionType;
        
        // Create load options with filters for both services
        // Only load trailer interventions if type is null (all), 'trailer', or 'manual'
        const shouldLoadTrailer = interventionType === null || interventionType === 'trailer' || interventionType === 'manual';
        const shouldLoadReconstructed = interventionType === null || interventionType === 'reconstructed';
        
        // Build trailer filter (includes manual interventions)
        const trailerFilter: any[] = [];
        if (fromDate && toDate) {
          trailerFilter.push(
            ['start_time', '>=', fromDate],
            'and',
            ['start_time', '<=', toDate]
          );
        }
        // Add manual filter if type is 'manual'
        if (interventionType === 'manual') {
          if (trailerFilter.length > 0) {
            trailerFilter.push('and');
          }
          trailerFilter.push(['is_manual', '=', true]);
        } else if (interventionType === 'trailer') {
          // When filtering for 'trailer', exclude manual interventions
          if (trailerFilter.length > 0) {
            trailerFilter.push('and');
          }
          trailerFilter.push(['is_manual', '=', false]);
        }
        
        const trailerLoadOptions: any = {
          filter: trailerFilter.length > 0 ? trailerFilter : undefined
        };

        // Build reconstructed filter
        const reconstructedFilter: any[] = [];
        // Skip date filters if showAllPending is true (show all pending regardless of date)
        if (!this.request.showAllPending && fromDate && toDate) {
          reconstructedFilter.push(
            ['start_time', '>=', fromDate],
            'and',
            ['start_time', '<=', toDate]
          );
        }
        if (interventionType === 'reconstructed') {
          if (reconstructedFilter.length > 0) {
            reconstructedFilter.push('and');
          }
          reconstructedFilter.push(['status', '=', 'pending_validation']);
        }
        
        const reconstructedLoadOptions: any = {
          filter: reconstructedFilter.length > 0 ? reconstructedFilter : undefined
        };

        // Load from both services using their stores
        // Use the public store getter for trailer service
        const trailerStore = this.trailerInterventionService.store;
        // Access the reconstructed service's custom store directly
        const reconstructedStore = (this.reconstructedInterventionService as any)._customStore;
        
        if (!reconstructedStore) {
          console.error('Reconstructed store not found');
          return Promise.resolve({ data: [], totalCount: 0 });
        }
        
        // Load from both stores (only if needed based on filter)
        const trailerPromise = shouldLoadTrailer ? trailerStore.load(trailerLoadOptions) : Promise.resolve({ data: [], totalCount: 0 });
        const reconstructedPromise = shouldLoadReconstructed ? reconstructedStore.load(reconstructedLoadOptions) : Promise.resolve({ data: [], totalCount: 0 });

        return Promise.all([trailerPromise, reconstructedPromise]).then((results: any[]) => {
          console.log('Load results:', results);
          const trailerResult = results[0] || { data: [] };
          const reconstructedResult = results[1] || { data: [] };

          console.log('Trailer result:', trailerResult);
          console.log('Reconstructed result:', reconstructedResult);
          console.log('Trailer data array:', trailerResult.data);
          console.log('Reconstructed data array:', reconstructedResult.data);

          // Transform trailer data - the store already returns TrailerIntervention objects
          const trailerData = (trailerResult.data || []).map((item: any) => {
            // Debug: Log raw item data for manual interventions
            if (item.is_manual) {
              console.log('Raw item from backend (before transformation):', {
                id: item.id,
                is_manual: item.is_manual,
                created_by_user_id: item.created_by_user_id,
                created_by_user_id_type: typeof item.created_by_user_id,
                created_at: item.created_at,
                item_keys: Object.keys(item)
              });
            }
            
            // Item might already be a TrailerIntervention instance or a plain object
            const intervention = item instanceof TrailerIntervention ? item : new TrailerIntervention(item);
            
            // Debug: Log after TrailerIntervention construction
            if (item.is_manual) {
              console.log('After TrailerIntervention construction:', {
                id: intervention.id,
                is_manual: (intervention as any).is_manual,
                created_by_user_id: (intervention as any).created_by_user_id,
                created_at: (intervention as any).created_at
              });
            }
            
            // Determine intervention source: manual if is_manual is true, otherwise trailer
            const interventionSource: 'manual' | 'trailer' = item.is_manual ? 'manual' : 'trailer';
            
            // Preserve raw item values first (these come directly from backend)
            // Then spread intervention to get all other properties
            const transformed = {
              ...intervention,
              // Override with raw item values to ensure backend data is preserved
              // These fields come directly from the backend SELECT query
              is_manual: item.is_manual !== undefined ? Boolean(item.is_manual) : (intervention.is_manual || false),
              created_by_user_id: item.created_by_user_id !== undefined ? (item.created_by_user_id !== null ? Number(item.created_by_user_id) : null) : (intervention.created_by_user_id !== undefined ? intervention.created_by_user_id : null),
              created_at: item.created_at !== undefined ? item.created_at : (intervention.created_at || null),
              data_accuracy_rating: item.data_accuracy_rating !== undefined ? item.data_accuracy_rating : (intervention.data_accuracy_rating || null),
              intervention_source: interventionSource
            };
            
            // Debug: Log final transformed data
            if (item.is_manual) {
              console.log('Final transformed data:', {
                id: transformed.id,
                is_manual: transformed.is_manual,
                created_by_user_id: transformed.created_by_user_id,
                created_by_user_id_type: typeof transformed.created_by_user_id,
                created_at: transformed.created_at,
                intervention_source: transformed.intervention_source
              });
            }
            
            return transformed;
          });

          // Transform reconstructed data (service already transforms it)
          const reconstructedData = (reconstructedResult.data || []).map((item: ReconstructedIntervention) => {
            // Map validation_accuracy to data_accuracy_rating for display consistency
            let mappedRating = null;
            if (item.validation_accuracy) {
              // Map validation_accuracy to data_accuracy_rating format
              if (item.validation_accuracy === 'CORRECT') {
                mappedRating = 'EXACT';
              } else if (item.validation_accuracy === 'APPROXIMATELY_CORRECT') {
                mappedRating = 'APPROXIMATELY_CORRECT';
              } else {
                mappedRating = 'INEXACT';
              }
            }
            return {
              ...item,
              intervention_source: 'reconstructed' as const,
              data_accuracy_rating: mappedRating // Map for consistent display
            };
          });

          console.log('Trailer data count:', trailerData.length);
          console.log('Reconstructed data count:', reconstructedData.length);
          console.log('Sample trailer item:', trailerData[0]);
          console.log('Sample reconstructed item:', reconstructedData[0]);

          // Merge and sort by start_time descending
          const merged = [...trailerData, ...reconstructedData].sort((a, b) => {
            const aTime = new Date(a.start_time).getTime();
            const bTime = new Date(b.start_time).getTime();
            return bTime - aTime;
          });

          console.log('Merged data count:', merged.length);
          console.log('Merged data sample:', merged.slice(0, 3));

          // Apply pagination after merging
          const skip = loadOptions.skip || 0;
          const take = loadOptions.take || 25;
          const paginated = merged.slice(skip, skip + take);

          return {
            data: paginated,
            totalCount: merged.length
          };
        }).catch((error) => {
          console.error('Error loading interventions:', error);
          console.error('Error details:', error);
          // Return empty data on error
          return {
            data: [],
            totalCount: 0
          };
        });
      }
    });

    this.ds = new DataSource({
      store: mergedStore,
      paginate: true,
      pageSize: 25,
      onLoadingChanged: (isLoading) => {
        setTimeout(() => {
          this.request.isLoading = isLoading;
          if (!isLoading) {
            // Trigger change detection when loading completes
            this.cdr.markForCheck();
          }
        }, 10);
      }
    });

    this.dateFormat = this.request.isToday ? 'shortTime' : 'shortDateShortTime';

    // Reload the data source
    this.ds.reload().then(() => {
      // Trigger change detection after reload
      this.cdr.markForCheck();
    });

    this.hasPendingRequest = false;
  }

  isReconstructed(intervention: any): boolean {
    return intervention?.intervention_source === 'reconstructed';
  }

  isTrailer(intervention: any): boolean {
    // Manual interventions are also trailer interventions (stored in trailer_interventions table)
    return intervention?.intervention_source === 'trailer' || intervention?.intervention_source === 'manual';
  }

  onActivated() {
    if (this.hasPendingRequest) {
      setTimeout(() => {
        this.loadRequest();
      }, 10);
    }
  }

  onDeactivated() {
  }

  ngOnDestroy(): void {
    if (this.submitSub) {
      this.submitSub.unsubscribe();
    }
  }

  getTagAttrValue(cell): any {
    const item = cell.data as TransportIntervention;
    const name = cell.column.caption;
    const attr = find(item.tag_details.attributes, { name });
    return attr ? attr.value : '-';
  }

  ngAfterViewInit(): void {
    this.submitSub = this.request.submit$.subscribe(() => {
      if (this.isActivated) {
        setTimeout(() => {
          this.loadRequest();
        }, 10);
      } else {
        this.hasPendingRequest = true;
      }
    });
  }

  onExporting(e) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Interventions Parcelles');

    exportDataGrid({
      component: e.component,
      worksheet,
      topLeftCell: { row: 2, column: 2 },
      customizeCell: ({ gridCell, excelCell }) => {

        if (gridCell.rowType === 'data') {

          if (gridCell.column.dataField.startsWith('attributes.')) {
            excelCell.value = this.getTagAttrValue(gridCell);
          }
        }
      }
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer: BlobPart) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'liste-interventions-transport.xlsx');
      });
    });
    e.cancel = true;
  }

  onToolbarPreparing(e: any) {
    e.toolbarOptions.items.push({
      location: 'before',
      template: 'columnFocusTabsTemplate',
    });
    
    // Find the first 'after' location item (should be column chooser or export)
    const firstAfterIndex = e.toolbarOptions.items.findIndex((item: any) => item.location === 'after');
    if (firstAfterIndex !== -1) {
      // Insert before the first 'after' item (which should be column chooser or export)
      e.toolbarOptions.items.splice(firstAfterIndex, 0, {
        location: 'after',
        template: 'addButtonTemplate',
        name: 'addButton'
      });
    } else {
      // Fallback: add at the beginning
      e.toolbarOptions.items.unshift({
        location: 'after',
        template: 'addButtonTemplate',
        name: 'addButton'
      });
    }
  }

  columnFocusSelectionChanged(e: any) {
    // Trigger change detection when column focus changes
    this.cdr.markForCheck();
  }

  hectareUnit(cellInfo) {
    return (parseFloat(cellInfo.value) / 10000).toFixed(2) + ' Ha';
  }

  widthUnit(cellInfo) {
    return (parseInt(cellInfo.value, 10) / 100).toFixed(1).replace(/\./g, ",");
  }

  percent(cellInfo) {
    if (!cellInfo.value) {
      return '-';
    }
    return (parseFloat(cellInfo.value) * 100).toFixed(2) + ' %';
  }

  duration(cellInfo) {
    return `<span style="text-decoration-style: dashed;" title="${durationToStr(cellInfo.value)}">${durationToHoursStr(cellInfo.value)} h</span>`;
  }

  calculateFilterExpression(filterValue, selectedFilterOperation) {
    const column = this as any;
    return [column.dataField, 'contains', filterValue];
  }

  CO2Emission = (rowData) => {
    if (rowData?.metrics?.fuel_total_consumption_all) {
      return (rowData.metrics.fuel_total_consumption_all * (720 + 1920)) / 1000;
    }
    return 0;
  }

  CalculateFuelTotal = (rowData) => {
    if (rowData?.metrics?.fuel_total_consumption_all) {
      return rowData.metrics.fuel_total_consumption_all / 2;
    } else {
      return '';
    }

  }

  literPerHour = (rowData) => {
    if (rowData?.fuel_total_consumption_per_hectare) {
      return rowData.fuel_total_consumption_per_hectare * 2;
    } else {
      return '';
    }

  }
  motorHectarPerHour = (rowData) => {
    if (rowData?.working_area_ha && rowData?.metrics?.engine_working_time_all) {
      return (rowData.working_area_ha / (rowData.metrics.engine_working_time_all / 3600));
    } else {
      return 0;
    }
  }

  // Helper methods for accuracy rating (trailer interventions)
  getAccuracyRatingLabel(rating: string): string {
    const option = this.accuracyRatingOptions.find(opt => opt.value === rating);
    return option ? option.label : rating;
  }

  getAccuracyRatingColor(rating: string | number): string {
    if (typeof rating === 'number') {
      // Handle numeric rating (0-10 scale)
      if (rating >= 8) return 'green';
      if (rating >= 5) return 'orange';
      return 'red';
    }
    // Handle string rating (EXACT, APPROXIMATELY_CORRECT, INEXACT)
    switch (rating) {
      case 'EXACT':
        return 'green';
      case 'APPROXIMATELY_CORRECT':
        return 'orange';
      case 'INEXACT':
        return 'red';
      default:
        return 'gray';
    }
  }

  getInterventionTypeColor(type: string): string {
    switch (type) {
      case 'reconstructed':
        return 'blue';
      case 'manual':
        return 'purple';
      case 'trailer':
      default:
        return 'green';
    }
  }

  getInterventionTypeLabel(type: string): string {
    switch (type) {
      case 'reconstructed':
        return 'Auto (A valider)';
      case 'manual':
        return 'Manuel';
      case 'trailer':
      default:
        return 'Auto';
    }
  }

  setRatingValue(rowData: any, value: any) {
    rowData.data_accuracy_rating = value;
  }

  onRatingUpdate(e: any) {
    e.cancel = true;
    
    if (e.newData && 'data_accuracy_rating' in e.newData && e.newData.data_accuracy_rating !== e.oldData.data_accuracy_rating) {
      const interventionId = e.key || e.oldData.id;
      const rating = e.newData.data_accuracy_rating;
      
      this.trailerInterventionService.updateDataAccuracyRating(interventionId, rating).subscribe({
        next: () => {
          if (e.data) {
            e.data.data_accuracy_rating = rating;
          }
          if (this.ds) {
            this.ds.reload();
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error updating data accuracy rating:', err);
          if (e.data) {
            e.data.data_accuracy_rating = e.oldData.data_accuracy_rating;
          }
        }
      });
    }
  }

  // Check if trailer can be edited
  canEditTrailer = (rowData: any) => {
    if (!rowData || !this.canEdit) {
      return false;
    }
    
    const intervention = rowData.intervention_source ? rowData : (rowData.row?.data || rowData.data || rowData);
    if (!intervention) {
      return false;
    }
    
    // Only allow editing for trailer interventions (including manual)
    const isTrailer = intervention.intervention_source === 'trailer' || intervention.intervention_source === 'manual';
    return isTrailer;
  }

  // Handler for trailer dropdown selection change - shows confirmation immediately
  onTrailerSelectChange(e: any, cellInfo: any) {
    const newTrailerId = e.value;
    const oldTrailerId = cellInfo.data?.trailer_id;
    
    // If same value, do nothing
    if (newTrailerId === oldTrailerId) {
      return;
    }
    
    const interventionId = cellInfo.data?.id;
    const oldTrailerName = cellInfo.data?.trailer_name || 'N/A';
    
    // Close the editor immediately
    if (this.grid?.instance) {
      this.grid.instance.cancelEditData();
    }
    
    // Get new trailer name for confirmation message
    this.trailersDataSource.load().then((trailers: any) => {
      const trailerList = Array.isArray(trailers) ? trailers : (trailers?.data || []);
      const newTrailer = trailerList.find((t: any) => t.id === newTrailerId);
      const newTrailerName = newTrailer?.name || 'Nouvel outil';
      
      // Show DevExtreme confirmation dialog
      const confirmDialog = custom({
        title: 'Confirmation de modification',
        messageHtml: `
          <div style="padding: 10px 0;">
            <p style="margin-bottom: 15px; font-size: 14px;">
              Êtes-vous sûr de vouloir modifier l'outil de cette intervention ?
            </p>
            <div style="background: #f8f9fa; padding: 12px; border-radius: 6px; border-left: 4px solid #007bff;">
              <div style="margin-bottom: 8px;">
                <strong style="color: #dc3545;">Ancien outil:</strong> 
                <span style="margin-left: 8px;">${oldTrailerName}</span>
              </div>
              <div>
                <strong style="color: #28a745;">Nouvel outil:</strong> 
                <span style="margin-left: 8px;">${newTrailerName}</span>
              </div>
            </div>
          </div>
        `,
        buttons: [
          {
            text: 'Confirmer',
            onClick: () => true,
            type: 'default',
            stylingMode: 'contained'
          },
          {
            text: 'Annuler',
            onClick: () => false,
            type: 'normal',
            stylingMode: 'outlined'
          }
        ]
      });

      confirmDialog.show().then((confirmed: boolean) => {
        if (!confirmed) {
          // User cancelled - grid already shows original value
          return;
        }
        
        // User confirmed - proceed with update
        this.trailerInterventionService.updateTrailer(interventionId, newTrailerId).subscribe({
          next: () => {
            notify('Outil mis à jour avec succès', 'success');
            // Reload to get updated trailer name
            if (this.ds) {
              this.ds.reload();
            }
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error updating trailer:', err);
            notify('Erreur lors de la mise à jour de l\'outil', 'error');
          }
        });
      });
    }).catch((err) => {
      console.error('Error loading trailers for confirmation:', err);
      notify('Erreur lors du chargement des outils', 'error');
    });
  }

  // Handler for row updates (for rating column only now)
  onTrailerUpdate(e: any) {
    // Handle data_accuracy_rating update
    if (e.newData && 'data_accuracy_rating' in e.newData && e.newData.data_accuracy_rating !== e.oldData.data_accuracy_rating) {
      e.cancel = true;
      this.onRatingUpdate(e);
    }
  }

  // Check if intervention is a trailer intervention (for rating button)
  // Now shows for ALL trailer interventions, allowing editing of existing ratings
  isTrailerForRating = (rowData: any) => {
    if (!rowData) {
      return false;
    }
    
    // For visible function, rowData is the actual row data object (has intervention_source property)
    // For onClick handlers, it's an event object with row.data
    const intervention = rowData.intervention_source ? rowData : (rowData.row?.data || rowData.data || rowData);
    if (!intervention) {
      return false;
    }
    
    // Show rating button for ALL trailer interventions (including manual)
    const isTrailer = intervention.intervention_source === 'trailer' || intervention.intervention_source === 'manual';
    
    return isTrailer;
  }
  
  // Check if user can delete manual intervention (created by user within 48 hours)
  canDeleteManualIntervention = (rowData: any) => {
    if (!rowData || !this.canDelete) {
      return false;
    }
    
    // Extract intervention data - handle different data formats
    const intervention = rowData.intervention_source ? rowData : (rowData.row?.data || rowData.data || rowData);
    if (!intervention) {
      return false;
    }
    
    // Debug logging (can be removed later)
    if (intervention.is_manual || intervention.intervention_source === 'manual') {
      console.log('canDeleteManualIntervention check:', {
        id: intervention.id,
        is_manual: intervention.is_manual,
        intervention_source: intervention.intervention_source,
        created_by_user_id: intervention.created_by_user_id,
        created_by_user_id_type: typeof intervention.created_by_user_id,
        current_user_id: this.user.id,
        current_user_id_type: typeof this.user.id,
        created_at: intervention.created_at,
        canDelete: this.canDelete,
        raw_intervention: intervention
      });
    }
    
    // Only manual interventions can be deleted
    // Check both intervention_source and is_manual flag
    const isManual = intervention.intervention_source === 'manual' || intervention.is_manual === true;
    if (!isManual) {
      return false;
    }
    
    // Check if user is the creator
    // Convert to number for comparison (backend might return as string or number)
    const createdByUserId = intervention.created_by_user_id;
    const currentUserId = this.user.id;
    
    // Handle both number and string types, and null/undefined
    if (createdByUserId === null || createdByUserId === undefined) {
      console.log('canDeleteManualIntervention: created_by_user_id is null/undefined');
      return false;
    }
    
    // Convert both to numbers for comparison
    const createdByUserIdNum = Number(createdByUserId);
    const currentUserIdNum = Number(currentUserId);
    
    if (isNaN(createdByUserIdNum) || isNaN(currentUserIdNum) || createdByUserIdNum !== currentUserIdNum) {
      console.log('canDeleteManualIntervention: user mismatch', {
        createdByUserId: createdByUserId,
        createdByUserIdNum: createdByUserIdNum,
        currentUserId: currentUserId,
        currentUserIdNum: currentUserIdNum
      });
      return false;
    }
    
    // Check if created_at exists and is valid
    const createdAtValue = intervention.created_at;
    if (!createdAtValue) {
      console.log('canDeleteManualIntervention: missing created_at');
      return false;
    }
    
    // Check if created within 48 hours
    try {
      const createdAt = new Date(createdAtValue);
      // Check if date is valid
      if (isNaN(createdAt.getTime())) {
        console.log('canDeleteManualIntervention: invalid date', createdAtValue);
        return false;
      }
      
      const now = new Date();
      const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
      
      // Allow deletion if within 48 hours (including exactly 48 hours)
      if (hoursSinceCreation <= 48) {
        console.log('canDeleteManualIntervention: CAN DELETE', { hoursSinceCreation });
        return true;
      } else {
        console.log('canDeleteManualIntervention: too old', { hoursSinceCreation });
      }
    } catch (error) {
      console.error('Error parsing created_at date:', error, createdAtValue);
      return false;
    }
    
    return false;
  }
  
  // Delete manual intervention
  deleteManualIntervention = (e: any) => {
    const intervention = e.row?.data || e.data;
    if (!intervention?.id) {
      notify('Intervention introuvable', 'error', 3000);
      return;
    }

    // Double-check permissions before showing confirmation
    if (!this.canDeleteManualIntervention(intervention)) {
      notify('Vous ne pouvez pas supprimer cette intervention', 'error', 3000);
      return;
    }

    // Confirm deletion
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette intervention manuelle ? Cette action est irréversible.')) {
      return;
    }

    // Call delete API (using remove method from service)
    // The backend will validate: manual intervention, creator, and 48-hour window
    this.trailerInterventionService.store.remove(intervention.id).then(() => {
      notify('Intervention supprimée avec succès', 'success', 3000);
      // Reload grid
      if (this.ds) {
        this.ds.reload();
      }
      this.cdr.markForCheck();
    }).catch((error: any) => {
      // Extract error message from backend response
      let errorMsg = 'Erreur lors de la suppression';
      if (error?.error?.error) {
        errorMsg = error.error.error;
      } else if (error?.error) {
        errorMsg = typeof error.error === 'string' ? error.error : error.error.error || errorMsg;
      } else if (error?.message) {
        errorMsg = error.message;
      }
      notify(errorMsg, 'error', 5000);
    });
  }

  // Check if intervention is pending validation (for reconstructed)
  // In DevExtreme buttons column, visible function receives row data directly (not event object)
  isPendingValidation = (rowData: any) => {
    if (!rowData) {
      return false;
    }
    
    // For visible function, rowData is the actual row data object (has intervention_source property)
    // For onClick handlers, it's an event object with row.data
    const intervention = rowData.intervention_source ? rowData : (rowData.row?.data || rowData.data || rowData);
    if (!intervention) {
      return false;
    }
    
    // Check if it's a reconstructed intervention with pending_validation status
    const isReconstructed = intervention.intervention_source === 'reconstructed';
    const isPending = intervention.status === 'pending_validation';
    
    // Debug logging
    if (isReconstructed) {
      console.log('isPendingValidation check:', {
        id: intervention.id,
        intervention_source: intervention.intervention_source,
        status: intervention.status,
        isPending,
        result: isReconstructed && isPending
      });
    }
    
    return isReconstructed && isPending;
  }

  // Validate intervention (reconstructed) - can also edit existing validation
  validateIntervention = (e: any) => {
    if (!this.canValidate) {
      notify('Vous n\'avez pas la permission de valider les interventions', 'error', 3000);
      return;
    }
    const intervention = e.row?.data || e.data;
    console.log('validateIntervention called:', { 
      intervention, 
      hasDialog: !!this.validateDialog, 
      hasId: !!intervention?.id 
    });
    if (!intervention?.id) {
      console.error('No intervention ID found:', intervention);
      return;
    }
    if (!this.validateDialog) {
      console.error('Validate dialog not found');
      return;
    }
    console.log('Calling validateDialog.show with ID:', intervention.id);
    
    // Pass existing validation data if intervention is validated
    const currentData = intervention.status === 'validated' ? {
      accuracy: intervention.validation_accuracy,
      type: intervention.intervention_type,
      cultureId: intervention.culture_id
    } : undefined;
    
    this.validateDialog.show(intervention.id, currentData);
    console.log('After show call, dialog visible:', this.validateDialog?.visible);
    // Trigger change detection to ensure dialog visibility updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  // Reject intervention (reconstructed)
  rejectIntervention = (e: any) => {
    if (!this.canReject) {
      notify('Vous n\'avez pas la permission de rejeter les interventions', 'error', 3000);
      return;
    }
    const intervention = e.row?.data || e.data;
    console.log('rejectIntervention called:', { 
      intervention, 
      hasDialog: !!this.rejectDialog, 
      hasId: !!intervention?.id 
    });
    if (!intervention?.id) {
      console.error('No intervention ID found:', intervention);
      return;
    }
    if (!this.rejectDialog) {
      console.error('Reject dialog not found');
      return;
    }
    console.log('Calling rejectDialog.show with ID:', intervention.id);
    this.rejectDialog.show(intervention.id);
    console.log('After show call, dialog visible:', this.rejectDialog?.visible);
    // Trigger change detection to ensure dialog visibility updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  // Check if intervention is validated or rejected (can be revoked)
  isValidatedOrRejected = (rowData: any) => {
    if (!rowData) {
      return false;
    }
    
    // For visible function, rowData is the actual row data object
    const intervention = rowData.intervention_source ? rowData : (rowData.row?.data || rowData.data || rowData);
    if (!intervention) {
      return false;
    }
    
    const isReconstructed = intervention.intervention_source === 'reconstructed';
    const isValidatedOrRejected = intervention.status === 'validated' || intervention.status === 'rejected';
    
    return isReconstructed && isValidatedOrRejected;
  }

  // Revoke intervention (reconstructed) - set back to pending_validation
  revokeIntervention = (e: any) => {
    if (!this.canRevoke) {
      notify('Vous n\'avez pas la permission de révoquer les interventions', 'error', 3000);
      return;
    }
    const intervention = e.row?.data || e.data;
    if (!intervention?.id) {
      notify('Intervention introuvable', 'error', 3000);
      return;
    }

    // Confirm action
    if (!confirm('Êtes-vous sûr de vouloir révoquer cette intervention ? Elle sera remise en attente de validation.')) {
      return;
    }

    // Call revoke API
    this.reconstructedInterventionService.revoke(intervention.id).subscribe({
      next: (response) => {
        notify('Intervention révoquée avec succès', 'success', 3000);
        // Reload grid
        if (this.ds) {
          this.ds.reload();
        }
      },
      error: (error) => {
        const errorMsg = error.error?.error || 'Erreur lors de la révocation';
        notify(errorMsg, 'error', 5000);
      }
    });
  }

  // Open rating dialog for trailer intervention
  openRatingDialog = (e: any) => {
    const intervention = e.row?.data || e.data;
    console.log('openRatingDialog called:', { intervention, hasId: !!intervention?.id, hasDialog: !!this.ratingDialog });
    if (!intervention?.id) {
      console.error('No intervention ID found:', intervention);
      return;
    }
    if (!this.ratingDialog) {
      console.error('Rating dialog not found');
      return;
    }
    console.log('Opening rating dialog for intervention:', intervention.id, 'current rating:', intervention.data_accuracy_rating);
    this.ratingDialog.show(intervention.id, intervention.data_accuracy_rating);
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  // Callbacks for validate/reject dialogs
  onValidateSuccess() {
    if (this.ds) {
      this.ds.reload();
    }
    this.cdr.markForCheck();
  }

  onRejectSuccess() {
    if (this.ds) {
      this.ds.reload();
    }
    this.cdr.markForCheck();
  }

  onRatingSuccess() {
    if (this.ds) {
      this.ds.reload();
    }
    this.cdr.markForCheck();
  }

  // Open manual intervention creation dialog
  openManualInterventionDialog = () => {
    if (!this.canAdd) {
      notify('Vous n\'avez pas la permission de créer des interventions', 'error', 3000);
      return;
    }
    if (!this.manualInterventionDialog) {
      console.error('Manual intervention dialog not found');
      return;
    }
    this.manualInterventionDialog.show();
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  // Callback for manual intervention creation
  onManualInterventionCreated() {
    if (this.ds) {
      this.ds.reload();
    }
    this.cdr.markForCheck();
  }
}
